import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'
import type { Theme } from '@emotion/react'
import { ClassNames, css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import Masonry from 'react-masonry-css'
import Link from 'next/link'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import Typography from '@segment/matcha/components/Typography'

import CardBase, * as Card from '@segment/matcha/recipes/Card'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

const breakpointColumnsObj = {
  default: 3,
  992: 2,
  576: 1,
}

const copy = {
  heading: `What do you want to learn?`,
  cta: `Show more`,
}

interface Card {
  cardHeading: string
  cardDescription: string
  cardUrl: string
}
interface LearnSectionProps {
  cards?: Card[]
}
const Learn: FC<PropsWithChildren<LearnSectionProps>> = ({ cards }) => {
  const theme = useTheme()
  const [limit, setLimit] = useState(6)

  return (
    <StyledSectionBase variant="centered">
      <Section.Copy>
        <Typography variant="h2">{copy.heading}</Typography>
      </Section.Copy>
      <Section.Contents>
        <ClassNames>
          {({ css }) => (
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className={css(masonryGrid)}
              columnClassName={css(getMasonryGridColumn(theme))}
            >
              {cards &&
                cards.slice(0, limit).map((card) => (
                  <Link href={card.cardUrl} key={card.cardHeading}>
                    <StyledCard tag="span">
                      <Card.Body>
                        <Card.Copy>
                          <Card.Headline tag="h3">{card.cardHeading}</Card.Headline>
                          <Card.Description variant="pLarge">{card.cardDescription}</Card.Description>
                        </Card.Copy>
                        <Card.Action alignment="right">
                          <ChevronRightIcon />
                        </Card.Action>
                      </Card.Body>
                    </StyledCard>
                  </Link>
                ))}
            </Masonry>
          )}
        </ClassNames>
        <CtaWrapper>
          {cards && limit <= cards.length && limit !== cards.length && (
            <ButtonText onClick={() => setLimit(cards.length)} iconEnd={ChevronRightIcon}>
              {copy.cta}
            </ButtonText>
          )}
        </CtaWrapper>
      </Section.Contents>
    </StyledSectionBase>
  )
}

export default Learn

const StyledSectionBase = styled(SectionBase)`
  max-width: 100%;
  ${Section.Wrapper} {
    background: ${({ theme }) => theme.palette.twilio.gray[10]};
  }
  ${Section.Contents} {
    max-width: 1440px;
  }
`

const masonryGrid = css`
  display: flex;
  flex-wrap: wrap;
  width: auto;
`

const getMasonryGridColumn = (theme: Theme) => css`
  ${theme.breakpoints.up(`sm`)} {
    padding: 0 16px;
  }
`

const StyledCard = styled(CardBase)`
  margin-bottom: 16px;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    margin-bottom: 32px;
  }
`

const CtaWrapper = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: center;
`
